import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ONBOARDING } from "@/constants/testIds";
import { toast, Toaster } from "sonner";
import { FileText, Sparkles, Trash2, ExternalLink, Download, Mail, X, BookOpen, CheckCircle2, RotateCcw, PenTool } from "lucide-react";

const DOC_TYPES = [
  { key: "service_agreement", label: "Service Agreement" },
  { key: "welcome_letter", label: "Welcome Letter" },
  { key: "kickoff_checklist", label: "Kickoff Checklist" },
];
const PLAN_OPTIONS = [
  { key: "starter", label: "Starter ($397/mo)", price: 397 },
  { key: "front_office", label: "Front Office ($697/mo)", price: 697 },
  { key: "multi_location", label: "Multi-Location ($997/mo)", price: 997 },
  { key: "custom", label: "Custom", price: 0 },
];

export default function Onboarding() {
  const [clients, setClients] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState("");
  const [docType, setDocType] = useState("service_agreement");
  const [plan, setPlan] = useState("starter");
  const [price, setPrice] = useState(397);
  const [setup, setSetup] = useState(0);
  const [busy, setBusy] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [emailDoc, setEmailDoc] = useState(null); // { id, label, client_id, client_name }
  const [emailForm, setEmailForm] = useState({ to: "", intro: "" });
  const [emailSending, setEmailSending] = useState(false);
  const [countersignDoc, setCountersignDoc] = useState(null);
  const [countersignForm, setCountersignForm] = useState({ full_name: "", typed_signature: "" });
  const [countersigning, setCountersigning] = useState(false);

  const load = async () => {
    try {
      const [{ data: c }, { data: d }] = await Promise.all([
        api.get("/clients"),
        api.get("/onboarding"),
      ]);
      setClients(c.clients || []);
      setDocs(d.documents || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onPlanChange = (p) => {
    setPlan(p);
    const found = PLAN_OPTIONS.find((x) => x.key === p);
    if (found && found.price) setPrice(found.price);
  };

  const generate = async () => {
    if (!clientId) { toast.error("Pick a client."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding/generate", {
        client_id: clientId, doc_type: docType, plan_tier: plan,
        monthly_price: Number(price) || 0, setup_fee: Number(setup) || 0,
      });
      const client = clients.find((c) => c.id === clientId);
      setDocs([{ ...data, client_name: client?.business_name || "" }, ...docs]);
      toast.success(`${DOC_TYPES.find(d => d.key === docType)?.label} generated.`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/onboarding/${id}`);
      setDocs(docs.filter((d) => d.id !== id));
      toast.success("Deleted.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const loadSamples = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/onboarding/seed-samples");
      const clientName = data.client?.business_name || "sample";
      const newDocs = (data.generated || []).map((d) => ({ ...d, client_name: clientName }));
      const skipCount = (data.skipped_existing || []).length;
      if (newDocs.length === 0 && skipCount > 0) {
        toast.info(`Sample docs already exist (${skipCount} found). Scroll the library.`);
      } else {
        setDocs([...newDocs, ...docs]);
        toast.success(`Generated ${newDocs.length} sample doc${newDocs.length === 1 ? "" : "s"} on "${clientName}".`);
      }
      // Refresh clients so the sample client appears in the dropdown
      const { data: c } = await api.get("/clients");
      setClients(c.clients || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSeeding(false); }
  };

  const openEmail = (doc) => {
    const client = clients.find((c) => c.id === doc.client_id);
    setEmailDoc({ ...doc, client_name: client?.business_name || doc.client_name || "" });
    setEmailForm({ to: client?.email || "", intro: "" });
  };

  const sendEmail = async () => {
    if (!emailDoc) return;
    setEmailSending(true);
    try {
      await api.post(`/onboarding/${emailDoc.id}/email`, {
        to: emailForm.to || null,
        intro: emailForm.intro,
      });
      toast.success(`Sent to ${emailForm.to}`);
      // Update doc row with last_emailed_to for visible feedback
      setDocs((rows) => rows.map((d) => d.id === emailDoc.id
        ? { ...d, last_emailed_to: emailForm.to, last_emailed_at: new Date().toISOString() }
        : d));
      setEmailDoc(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setEmailSending(false); }
  };

  const unsign = async (doc) => {
    if (!window.confirm(`Revoke signature on "${doc.label}"? The original signer info is kept for audit.`)) return;
    try {
      await api.delete(`/onboarding/${doc.id}/sign`);
      setDocs((rows) => rows.map((d) => d.id === doc.id ? { ...d, signature: null, countersignature: null } : d));
      toast.success("Signature revoked.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openCountersign = (doc) => {
    setCountersignDoc(doc);
    setCountersignForm({ full_name: "", typed_signature: "" });
  };

  const submitCountersign = async () => {
    if (!countersignDoc) return;
    setCountersigning(true);
    try {
      const { data } = await api.post(`/onboarding/${countersignDoc.id}/countersign`, countersignForm);
      setDocs((rows) => rows.map((d) => d.id === countersignDoc.id
        ? { ...d, countersignature: { full_name: countersignForm.full_name, signed_at: data.countersignature.signed_at } }
        : d));
      toast.success("Countersigned — client has been emailed the executed agreement.");
      setCountersignDoc(null);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCountersigning(false); }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  return (
    <div data-testid={ONBOARDING.container} className="p-10">
      <Toaster position="top-right" richColors />
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Back office</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Onboarding documents</h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">
            Generate professional agreements and welcome letters for new clients — powered by Claude Sonnet.
            Email them straight to the client, or download as PDF.
          </p>
        </div>
        <button
          data-testid="onboarding-load-samples-btn"
          onClick={loadSamples}
          disabled={seeding}
          className="lf-btn-outline flex items-center gap-2 text-sm"
        >
          <BookOpen className="w-4 h-4" />
          {seeding ? "Generating…" : "Preview sample docs"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: generator */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-neutral-200 p-6 sticky top-6">
            <div className="overline mb-4">Generate new</div>
            <div className="space-y-4">
              <Field label="Client">
                <select
                  data-testid={ONBOARDING.clientSelect}
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  className="lf-input"
                >
                  <option value="">Select a client…</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.business_name}</option>
                  ))}
                </select>
                {clients.length === 0 && (
                  <p className="text-xs text-neutral-500 mt-2">
                    No clients yet — <Link to="/clients" className="border-b border-black">add one</Link> first.
                  </p>
                )}
              </Field>
              <Field label="Document type">
                <select
                  data-testid={ONBOARDING.docTypeSelect}
                  value={docType}
                  onChange={(e) => setDocType(e.target.value)}
                  className="lf-input"
                >
                  {DOC_TYPES.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                </select>
              </Field>
              <Field label="Plan tier">
                <select
                  data-testid={ONBOARDING.planSelect}
                  value={plan}
                  onChange={(e) => onPlanChange(e.target.value)}
                  className="lf-input"
                >
                  {PLAN_OPTIONS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Monthly price">
                  <input
                    data-testid={ONBOARDING.priceInput}
                    type="number" min="0" value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="lf-input"
                  />
                </Field>
                <Field label="Setup fee">
                  <input
                    data-testid={ONBOARDING.setupInput}
                    type="number" min="0" value={setup}
                    onChange={(e) => setSetup(e.target.value)}
                    className="lf-input"
                  />
                </Field>
              </div>
              <button
                data-testid={ONBOARDING.generateBtn}
                onClick={generate}
                disabled={busy || !clientId}
                className="lf-btn-primary w-full flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                {busy ? "Generating with AI…" : "Generate document"}
              </button>
              <p className="text-xs text-neutral-500 leading-relaxed">
                Uses Claude Sonnet 4.6 via the Emergent LLM key. Takes about 10-20 seconds.
              </p>
            </div>
          </div>
        </div>

        {/* Right: doc list */}
        <div className="lg:col-span-2">
          <div className="overline mb-4">Library</div>
          {loading ? (
            <div className="text-neutral-500">Loading…</div>
          ) : docs.length === 0 ? (
            <div className="border border-dashed border-neutral-300 bg-white p-12 text-center">
              <FileText className="w-8 h-8 mx-auto mb-3 text-neutral-400" />
              <div className="font-display text-xl mb-2">No documents yet</div>
              <p className="text-neutral-500 text-sm">Pick a client, choose a document type, and hit generate.</p>
            </div>
          ) : (
            <div className="bg-white border border-neutral-200" data-testid={ONBOARDING.docsTable}>
              <table className="lf-grid">
                <thead>
                  <tr>
                    <th>Document</th><th>Client</th><th>Plan</th><th>Created</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d) => (
                    <tr key={d.id}>
                      <td className="font-medium">
                        <FileText className="w-4 h-4 inline-block mr-2 text-neutral-400" />
                        {d.label}
                        {d.signature?.signed_at && !d.countersignature?.signed_at && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs px-1.5 py-0.5 bg-green-50 border border-green-200 text-green-800 font-mono uppercase tracking-wider" data-testid={`onboarding-signed-badge-${d.id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Signed
                          </span>
                        )}
                        {d.countersignature?.signed_at && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs px-1.5 py-0.5 bg-black text-amber-400 font-mono uppercase tracking-wider" data-testid={`onboarding-executed-badge-${d.id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Executed
                          </span>
                        )}
                        {d.signature?.signed_at && (
                          <div className="text-xs text-neutral-500 font-mono mt-1 ml-6">
                            by {d.signature.full_name} · {new Date(d.signature.signed_at).toLocaleDateString()}
                            {d.countersignature?.signed_at && (
                              <span className="ml-2 text-amber-700">· countersigned by {d.countersignature.full_name}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="text-sm">{d.client_name || "—"}</td>
                      <td className="text-sm font-mono uppercase">{d.plan_tier}</td>
                      <td className="text-sm font-mono">{new Date(d.created_at).toLocaleDateString()}</td>
                      <td className="text-right">
                        <div className="inline-flex items-center gap-3">
                          <a
                            data-testid={ONBOARDING.viewBtn(d.id)}
                            href={`${backendUrl}/api/onboarding/view/${d.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm font-medium hover:underline inline-flex items-center gap-1"
                          >
                            View <ExternalLink className="w-3 h-3" />
                          </a>
                          <a
                            href={`${backendUrl}/api/onboarding/pdf/${d.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm font-medium hover:underline inline-flex items-center gap-1"
                          >
                            PDF <Download className="w-3 h-3" />
                          </a>
                          <button
                            data-testid={`onboarding-email-btn-${d.id}`}
                            onClick={() => openEmail(d)}
                            className="text-sm font-medium hover:underline inline-flex items-center gap-1"
                            title={d.last_emailed_to ? `Last sent to ${d.last_emailed_to}` : "Email to client"}
                          >
                            {d.last_emailed_to ? "Re-email" : "Email"} <Mail className="w-3 h-3" />
                          </button>
                          {d.signature?.signed_at && !d.countersignature?.signed_at && (
                            <button
                              data-testid={`onboarding-countersign-btn-${d.id}`}
                              onClick={() => openCountersign(d)}
                              className="text-sm font-medium text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"
                              title="Countersign as Clients OnRamp — executes the agreement"
                            >
                              Countersign <PenTool className="w-3 h-3" />
                            </button>
                          )}
                          {d.signature?.signed_at && (
                            <button
                              data-testid={`onboarding-unsign-btn-${d.id}`}
                              onClick={() => unsign(d)}
                              className="text-sm font-medium text-neutral-500 hover:text-red-700 inline-flex items-center gap-1"
                              title="Revoke signature (kept for audit)"
                            >
                              Unsign <RotateCcw className="w-3 h-3" />
                            </button>
                          )}
                          <button
                            data-testid={ONBOARDING.deleteBtn(d.id)}
                            onClick={() => remove(d.id)}
                            className="text-neutral-400 hover:text-red-600 transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Email doc modal */}
      {emailDoc && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          data-testid="onboarding-email-modal"
          onClick={() => setEmailDoc(null)}
        >
          <div
            className="bg-white border border-neutral-200 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between">
              <div>
                <div className="overline">Email doc to client</div>
                <div className="text-lg font-bold mt-1">{emailDoc.label}</div>
                <div className="text-xs text-neutral-500 mt-0.5">{emailDoc.client_name || "(no client name)"}</div>
              </div>
              <button
                onClick={() => setEmailDoc(null)}
                data-testid="onboarding-email-modal-close"
                className="text-neutral-400 hover:text-black"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Send to email">
                <input
                  data-testid="onboarding-email-to-input"
                  type="email"
                  value={emailForm.to}
                  onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                  placeholder="client@example.com"
                  className="lf-input"
                />
                <p className="text-xs text-neutral-500 mt-1.5">
                  Pre-filled from the client record. Change it to override.
                </p>
              </Field>
              <Field label="Personal note (optional)">
                <textarea
                  data-testid="onboarding-email-intro-input"
                  value={emailForm.intro}
                  onChange={(e) => setEmailForm({ ...emailForm, intro: e.target.value })}
                  rows={4}
                  placeholder="Great meeting you today. Here's the agreement we discussed…"
                  className="lf-input"
                />
              </Field>
              <div className="text-xs text-neutral-500 bg-neutral-50 p-3 border border-neutral-100">
                Email includes read-online link + PDF download. Sent via Resend from your configured domain.
              </div>
            </div>
            <div className="px-6 py-4 border-t border-neutral-100 flex justify-end gap-2">
              <button
                onClick={() => setEmailDoc(null)}
                className="lf-btn-outline text-sm"
              >
                Cancel
              </button>
              <button
                data-testid="onboarding-email-send-btn"
                onClick={sendEmail}
                disabled={emailSending || !emailForm.to}
                className="lf-btn-primary text-sm flex items-center gap-2"
              >
                <Mail className="w-4 h-4" />
                {emailSending ? "Sending…" : "Send email"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Countersign modal */}
      {countersignDoc && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          data-testid="onboarding-countersign-modal"
          onClick={() => setCountersignDoc(null)}
        >
          <div
            className="bg-white border border-neutral-200 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between">
              <div>
                <div className="overline">Countersign as Clients OnRamp</div>
                <div className="text-lg font-bold mt-1">{countersignDoc.label}</div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  Client signed by {countersignDoc.signature?.full_name} · {countersignDoc.signature?.signed_at && new Date(countersignDoc.signature.signed_at).toLocaleDateString()}
                </div>
              </div>
              <button
                onClick={() => setCountersignDoc(null)}
                data-testid="onboarding-countersign-modal-close"
                className="text-neutral-400 hover:text-black"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="Your full name">
                <input
                  data-testid="onboarding-countersign-name-input"
                  type="text"
                  value={countersignForm.full_name}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCountersignForm((f) => ({
                      full_name: v,
                      typed_signature: f.typed_signature || v,
                    }));
                  }}
                  placeholder="e.g. Your name"
                  className="lf-input"
                />
              </Field>
              <Field label="Typed signature">
                <input
                  data-testid="onboarding-countersign-sig-input"
                  type="text"
                  value={countersignForm.typed_signature}
                  onChange={(e) => setCountersignForm({ ...countersignForm, typed_signature: e.target.value })}
                  placeholder="Type your name to sign"
                  className="lf-input"
                  style={{ fontFamily: '"Segoe Script","Bradley Hand",cursive', fontSize: 20, background: "#FFFBEB", borderColor: "#FCD34D" }}
                />
              </Field>
              <div className="text-xs text-neutral-600 bg-amber-50 border border-amber-200 p-3">
                <strong>What happens next:</strong> This locks in a fully executed two-party agreement. Client is automatically emailed the countersigned copy with view + PDF links.
              </div>
            </div>
            <div className="px-6 py-4 border-t border-neutral-100 flex justify-end gap-2">
              <button onClick={() => setCountersignDoc(null)} className="lf-btn-outline text-sm">Cancel</button>
              <button
                data-testid="onboarding-countersign-submit-btn"
                onClick={submitCountersign}
                disabled={countersigning || !countersignForm.full_name.trim() || !countersignForm.typed_signature.trim()}
                className="lf-btn-primary text-sm flex items-center gap-2"
              >
                <PenTool className="w-4 h-4" />
                {countersigning ? "Signing…" : "Execute agreement"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
